import React, { useEffect, useState } from "react";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/auth/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Fill all fields");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/users", form);
      setUsers((prev) => [...prev, data]);
      setModal(false);
      setForm({ name: "", email: "", password: "", role: "admin" });
      toast.success("Admin added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl font-bold text-sapore-purple">Admin Users</h1>
        <button data-testid="add-user-button" onClick={() => setModal(true)} className="px-5 py-2.5 rounded-full bg-sapore-gold text-sapore-ink font-semibold flex items-center gap-2"><Plus size={16} /> Add admin</button>
      </div>
      <div className="bg-white rounded-2xl border border-sapore-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sapore-creamSurface text-left text-sapore-muted"><tr><th className="py-3 px-4">Name</th><th className="py-3 px-4">Email</th><th className="py-3 px-4">Role</th><th className="py-3 px-4"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-sapore-border/60">
                <td className="py-3 px-4">{u.name}</td>
                <td className="py-3 px-4">{u.email}</td>
                <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 rounded-full bg-sapore-goldSubtle">{u.role}</span></td>
                <td className="py-3 px-4 text-right">{u.id !== user.id && <button onClick={() => remove(u)} className="text-red-500"><Trash2 size={16} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-xl text-sapore-purple">Add admin user</DialogTitle></DialogHeader>
          <input data-testid="new-user-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-sapore-border p-3 text-sm mb-3" />
          <input data-testid="new-user-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-sapore-border p-3 text-sm mb-3" />
          <input data-testid="new-user-password" placeholder="Temporary password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-xl border border-sapore-border p-3 text-sm mb-3" />
          <select data-testid="new-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full rounded-xl border border-sapore-border p-3 text-sm mb-4 bg-white">
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
          <button data-testid="save-user-button" onClick={add} disabled={loading} className="w-full py-3 rounded-full bg-sapore-gold text-sapore-ink font-semibold flex items-center justify-center gap-2 disabled:opacity-50">{loading && <Loader2 size={16} className="animate-spin" />} Create</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
